import React, { useMemo } from 'react';
import { Line } from '@react-three/drei';
import useStore from '../store';
import { projectTo3D } from '../utils/coordinates';
import { filterNodes } from '../graphLoader';

function EdgeMesh({ edges, nodes }) {
  const { filters } = useStore();
  
  const filteredEdges = useMemo(() => {
    // Only show edges if filters are active
    const hasActiveFilters = (
      filters.searchQuery ||
      filters.categories.length > 0 ||
      filters.agencies.length > 0 ||
      filters.minValue > 0 ||
      filters.maxValue < 100000000
    );
    
    if (!hasActiveFilters) return [];
    
    // Get filtered nodes
    const filteredNodesList = filterNodes(nodes, filters);
    const filteredNodeIds = new Set(filteredNodesList.map(n => n.id));
    
    // Only show edges between filtered nodes
    return edges.filter(edge => 
      filteredNodeIds.has(edge.source) && filteredNodeIds.has(edge.target)
    );
  }, [edges, nodes, filters]);
  
  const edgeLines = useMemo(() => {
    const nodeMap = new Map(nodes.map(n => [n.id, n]));
    
    return filteredEdges.map(edge => {
      const sourceNode = nodeMap.get(edge.source);
      const targetNode = nodeMap.get(edge.target);
      
      if (!sourceNode || !targetNode) return null;
      
      const start = projectTo3D(sourceNode.lat, sourceNode.lon, 0.01);
      const end = projectTo3D(targetNode.lat, targetNode.lon, 0.01);
      
      return {
        id: `${edge.source}-${edge.target}`,
        points: [start, end],
        weight: edge.weight || 1
      };
    }).filter(Boolean);
  }, [filteredEdges, nodes]);
  
  if (edgeLines.length === 0) return null;
  
  return (
    <group>
      {edgeLines.map(edge => (
        <Line
          key={edge.id}
          points={edge.points}
          color="#05f3ff"
          lineWidth={0.5 * edge.weight}
          transparent
          opacity={0.15}
        />
      ))}
    </group>
  );
}

export default EdgeMesh;